/**
 * Inspection hooks — TanStack Query for check-in/check-out inspections.
 *
 * Query key factory, data fetching, and mutation with photo progress tracking.
 */
import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inspectionService } from '../services/inspection.service'
import { bookingKeys } from './use-booking'
import type {
  InspectionType,
  InspectionDetail,
  InspectionFormData,
} from '../types/inspection.types'

// ─── Query Key Factory ─────────────────────────────────────────────────────

const inspectionKeys = {
  all: ['inspections'] as const,
  detail: (rentalId: string, type: InspectionType) =>
    [...inspectionKeys.all, 'detail', rentalId, type] as const,
  comparison: (rentalId: string) => [...inspectionKeys.all, 'comparison', rentalId] as const,
}

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * Fetch a single inspection (check-in or check-out) for a rental.
 */
export function useInspection(rentalId: string | undefined, type: InspectionType) {
  return useQuery<InspectionDetail | null>({
    queryKey: inspectionKeys.detail(rentalId ?? '', type),
    queryFn: () => inspectionService.getInspection(rentalId!, type),
    enabled: !!rentalId,
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────

interface CreateInspectionVars {
  rentalId: string
  userId: string
  type: InspectionType
  formData: InspectionFormData
}

/**
 * Create an inspection with photo upload progress tracking.
 *
 * @returns mutation + photoProgress (0..total) + totalPhotos
 */
export function useCreateInspection() {
  const queryClient = useQueryClient()
  const [photoProgress, setPhotoProgress] = useState(0)
  const [totalPhotos, setTotalPhotos] = useState(0)

  const onPhotoProgress = useCallback((completed: number, total: number) => {
    setPhotoProgress(completed)
    setTotalPhotos(total)
  }, [])

  const mutation = useMutation<InspectionDetail, Error, CreateInspectionVars>({
    mutationFn: ({ rentalId, userId, type, formData }) => {
      setPhotoProgress(0)
      setTotalPhotos(formData.photos.length)
      return inspectionService.createInspection(rentalId, userId, type, formData, onPhotoProgress)
    },
    onSuccess: (_data, vars) => {
      // Invalidate inspection queries
      void queryClient.invalidateQueries({
        queryKey: inspectionKeys.detail(vars.rentalId, vars.type),
      })
      void queryClient.invalidateQueries({
        queryKey: inspectionKeys.comparison(vars.rentalId),
      })
      /*
       * Y la reserva, que es lo que la persona vuelve a ver.
       *
       * Aca decia `['trips']` y `['rentals']`. Ninguna consulta del repo usa
       * esos prefijos --las dos unicas coincidencias en todo el codigo eran
       * estas dos lineas--, asi que eran invalidaciones a la nada. El detalle
       * vive bajo `bookingKeys` con `staleTime` de 2 minutos, de modo que
       * despues de una entrega exitosa la ficha seguia diciendo "En curso" y
       * volvia a ofrecer el MISMO boton para una accion irreversible que ya se
       * hizo. Se invalida la raiz porque el estado de la renta tambien cambia
       * las listas ("mis viajes", "por entregar"), no solo el detalle.
       */
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all })
    },
  })

  return {
    ...mutation,
    photoProgress,
    totalPhotos,
  }
}
